import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, Bell, Star, Send, AudioLines, Printer, Repeat, Megaphone, Flame, ChevronRight, Wrench, Layers, Users, Shield, ALargeSmall, Settings as SettingsIcon } from 'lucide-react';
import { TEXT_SCALES, getTextScale, applyTextScale } from '../lib/textScale';
import { useAuth } from '../contexts/AuthContext';
import { MsTodoCard } from '../components/MsTodoCard';
import { PlaudSettingsDialog } from '../components/PlaudSettingsDialog';
import { PrintSettingsDialog } from '../components/PrintSettingsDialog';

const TRIGGER_INFO = [
  { key: 'stage_change', icon: '🔄', label: 'Stage Change', desc: 'When a favorited deal moves to a new stage' },
  { key: 'new_event', icon: '📅', label: 'New Meeting / Call', desc: 'When a new meeting or call is logged on a favorited deal' },
  { key: 'new_comment', icon: '💬', label: 'New Comment', desc: 'When someone adds a comment or note on a favorited deal — @-mentions always notify you' },
  { key: 'new_edit', icon: '✏️', label: 'New Edit', desc: 'When deal info is updated (financials, scores, exec summary, letters, etc.)' },
];

const SYSTEM_TRIGGER_INFO = [
  { key: 'deal_proposal', icon: '🤖', label: 'AI Deal Proposals', desc: 'When the inbox parser detects a new deal or teaser in your emails' },
  { key: 'sequence', icon: '📤', label: 'Sequences & Broker Campaigns', desc: 'Outreach steps due, broker replies, bounces and auto-paused sequences/campaigns' },
  { key: 'outreach', icon: '✉️', label: 'Outreach Letters', desc: 'Letters sent back for changes and follow-up calls that are due' },
  { key: 'landing', icon: '👀', label: 'Landing Page Engagement', desc: 'QR scans, repeat visits, video plays, opt-outs, hot leads and booked meetings' },
  { key: 'printing', icon: '🖨️', label: 'Printing (iAuto & Printclub)', desc: 'Letter written / print errors on the iAuto and Printclub order status' },
  { key: 'nda', icon: '📄', label: 'NDA', desc: 'When an NDA is sent, opened or signed' },
  { key: 'meetings', icon: '🗓️', label: 'Team Meeting Reminders', desc: 'Agenda-email reminders and finalize warnings for the team meeting' },
  { key: 'ac', icon: '🏛️', label: 'Advisory Committee', desc: 'AC feedback received and automatic AC agenda changes' },
  { key: 'task_assigned', icon: '✅', label: 'Tasks', desc: 'Task assignments and Microsoft To-Do sync warnings' },
  { key: 'webex', icon: '📞', label: 'Broker Calls (Webex)', desc: 'When a Webex call with a known broker/owner contact is detected and logged' },
  { key: 'monday_digest', icon: '🌅', label: 'Monday Check-in', desc: 'The weekly Monday morning digest push' },
  { key: 'ai_budget', icon: '💰', label: 'AI Budget Alerts', desc: 'When the AI usage budget crosses its threshold (admins)' },
  { key: 'security', icon: '🔐', label: 'Security Alerts', desc: 'Suspicious logins and other security-related warnings' },
];

export default function SettingsPage() {
  const { user, isAdmin, isAcMember } = useAuth();
  // AC members don't get the Microsoft connection — except the shared kontakt@ account.
  const showMsCard = !isAcMember || (user?.email || '').toLowerCase() === 'kontakt@olta-nachfolge.com';
  const params = new URLSearchParams(window.location.search);
  const [tab, setTab] = useState(params.get('tab') || (params.has('mstodo') ? 'integrations' : 'personal'));

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl text-foreground tracking-tight">Einstellungen</h1>
        <p className="text-sm text-muted-foreground mt-1">Benachrichtigungen, Integrationen und Automationen — alles an einem Ort</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-secondary/50 border border-border rounded-sm h-9">
          <TabsTrigger value="personal" data-testid="settings-tab-personal" className="text-xs rounded-sm">Persönlich</TabsTrigger>
          {showMsCard && <TabsTrigger value="integrations" data-testid="settings-tab-integrations" className="text-xs rounded-sm">Integrationen</TabsTrigger>}
          {!isAcMember && <TabsTrigger value="automations" data-testid="settings-tab-automations" className="text-xs rounded-sm">Automationen</TabsTrigger>}
          {isAdmin && <TabsTrigger value="system" data-testid="settings-tab-system" className="text-xs rounded-sm">System</TabsTrigger>}
        </TabsList>

        <TabsContent value="personal" className="mt-4 space-y-4">
          <TextSizeCard />
          <NotificationSettings isAcMember={isAcMember} />
        </TabsContent>

        {showMsCard && (
          <TabsContent value="integrations" className="mt-4">
            <IntegrationsTab showMsCard={showMsCard} isAcMember={isAcMember} />
          </TabsContent>
        )}

        {!isAcMember && (
          <TabsContent value="automations" className="mt-4">
            <AutomationsTab />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="system" className="mt-4">
            <SystemTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/* ─────────────────────────── Textgröße ─────────────────────────── */

function TextSizeCard() {
  const [scale, setScale] = useState(getTextScale());
  const pick = (v) => { setScale(v); applyTextScale(v); };
  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="text-size-card">
      <div className="flex items-start gap-3">
        <ALargeSmall className="w-5 h-5 shrink-0 mt-0.5 text-sky-400" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">Textgröße</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Skaliert die gesamte App auf diesem Gerät — wird pro Gerät gespeichert
          </p>
          <div className="flex items-center gap-2 mt-3">
            {TEXT_SCALES.map(t => (
              <button key={t.value} onClick={() => pick(t.value)} data-testid={`text-scale-${t.label}`}
                title={t.desc}
                className={`min-w-[52px] px-3 py-2 rounded-sm border text-center transition-colors ${scale === t.value
                  ? 'border-sky-500/60 bg-sky-500/10 text-sky-300'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'}`}>
                <span className="font-semibold" style={{ fontSize: `${Math.round(13 * parseFloat(t.value))}px` }}>{t.label}</span>
                <span className="block text-[9px] mt-0.5 opacity-70">{t.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Integrationen ─────────────────────────── */

function IntegrationRow({ icon: Icon, iconClass, title, desc, btnLabel, onOpen, testid }) {
  return (
    <div className="bg-card border border-border rounded-sm p-4 flex items-center justify-between gap-4">
      <div className="flex items-start gap-3 min-w-0">
        <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${iconClass}`} strokeWidth={1.5} />
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
        </div>
      </div>
      <Button onClick={onOpen} variant="outline" data-testid={testid}
        className="shrink-0 border-border text-foreground rounded-sm text-xs h-8 px-3">
        {btnLabel}
      </Button>
    </div>
  );
}

function IntegrationsTab({ showMsCard, isAcMember }) {
  const [plaudOpen, setPlaudOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [printSettings, setPrintSettings] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);

  const openPrint = async () => {
    setPrintLoading(true);
    try {
      const r = await api.printSettings();
      setPrintSettings(r.data);
      setPrintOpen(true);
    } catch { toast.error('iAuto-Einstellungen konnten nicht geladen werden'); }
    setPrintLoading(false);
  };

  return (
    <div className="space-y-4" data-testid="settings-integrations">
      {showMsCard && <MsTodoCard />}
      {!isAcMember && (
        <>
          <IntegrationRow icon={AudioLines} iconClass="text-violet-400" title="Plaud AI"
            desc="Aufnahmegerät verbinden — Gesprächsnotizen werden automatisch importiert und Calls/Meetings zugeordnet."
            btnLabel="Konfigurieren" onOpen={() => setPlaudOpen(true)} testid="settings-open-plaud" />
          <IntegrationRow icon={Printer} iconClass="text-sky-400" title="iAuto Schreibmaschine"
            desc={printLoading ? 'Lade Einstellungen…' : 'Handschrift, Positionen, Schreibgeschwindigkeit und Agent-Key für den lokalen Brief-/Flyer-Druck.'}
            btnLabel={printLoading ? '…' : 'Konfigurieren'} onOpen={openPrint} testid="settings-open-iauto" />
        </>
      )}
      <PlaudSettingsDialog open={plaudOpen} onOpenChange={setPlaudOpen} onChanged={() => {}} />
      {printSettings && (
        <PrintSettingsDialog open={printOpen} onOpenChange={setPrintOpen} settings={printSettings}
          onSaved={() => api.printSettings().then(r => setPrintSettings(r.data)).catch(() => {})} />
      )}
    </div>
  );
}

/* ─────────────────────────── Automationen ─────────────────────────── */

function AutomationsTab() {
  const navigate = useNavigate();
  return (
    <div className="space-y-4" data-testid="settings-automations">
      <CheckinAutomationCard />

      <button onClick={() => navigate('/outreach-sequences')} data-testid="settings-goto-sequences"
        className="w-full bg-card border border-border rounded-sm p-4 flex items-center justify-between gap-4 hover:bg-secondary/30 transition-colors text-left">
        <div className="flex items-start gap-3">
          <Repeat className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="text-sm font-medium text-foreground">Sequenzen-Templates</p>
            <p className="text-xs text-muted-foreground mt-0.5">Schritte, Wartezeiten und Texte der Eigentümer-Sequenzen (Brief → E-Mail → Call) bearbeiten.</p>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </button>

      <button onClick={() => navigate('/broker-campaigns')} data-testid="settings-goto-campaigns"
        className="w-full bg-card border border-border rounded-sm p-4 flex items-center justify-between gap-4 hover:bg-secondary/30 transition-colors text-left">
        <div className="flex items-start gap-3">
          <Megaphone className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="text-sm font-medium text-foreground">Broker-Kampagnen</p>
            <p className="text-xs text-muted-foreground mt-0.5">Automatisierte E-Mail-Kampagnen an Broker verwalten und starten.</p>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </button>

      <div className="bg-secondary/30 border border-border rounded-sm p-4">
        <p className="text-xs text-muted-foreground">
          <span className="text-foreground font-medium">Automatische Eingangsbestätigung (72h) an Broker:</span> deaktiviert.
          Aktive automatische Broker-Mails sind nur der Check-in-Autoversand (oben) und laufende Kampagnen.
        </p>
      </div>
    </div>
  );
}

function CheckinAutomationCard() {
  const [loaded, setLoaded] = useState(false);
  const [autosend, setAutosend] = useState(false);
  const [minDays, setMinDays] = useState('30');
  const [maxDays, setMaxDays] = useState('45');
  const [weeks, setWeeks] = useState('4');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getDiscussionSettings().then(r => {
      setAutosend(!!r.data.checkin_autosend);
      setMinDays(String(r.data.checkin_min_days || 30));
      setMaxDays(String(r.data.checkin_max_days || 45));
      setWeeks(String(r.data.checkin_weeks || 4));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.saveDiscussionSettings(autosend
        ? { checkin_autosend: true, checkin_min_days: parseInt(minDays, 10) || 30, checkin_max_days: parseInt(maxDays, 10) || 45 }
        : { checkin_autosend: false, checkin_weeks: parseInt(weeks, 10) || 4 });
      toast.success('Check-in-Einstellungen gespeichert');
    } catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving(false);
  };

  return (
    <div className="bg-card border border-border rounded-sm p-4 space-y-4" data-testid="settings-checkin-card">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Flame className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="text-sm font-medium text-foreground">Broker-Check-ins</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              KI schreibt Follow-ups an 🔥-Broker und Broker mit komplett durchlaufener Sequenz — automatisch versenden oder als Entwurf vorlegen.
            </p>
          </div>
        </div>
        {!loaded ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : (
          <Switch checked={autosend} onCheckedChange={setAutosend} data-testid="settings-checkin-autosend" />
        )}
      </div>
      {loaded && (autosend ? (
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground block mb-1">Frühestens nach (Tagen)</label>
            <Input type="number" min="7" max="120" value={minDays} onChange={e => setMinDays(e.target.value)}
              data-testid="settings-checkin-min-days" className="bg-secondary border-border text-foreground rounded-sm h-9" />
          </div>
          <div className="flex-1">
            <label className="text-xs text-muted-foreground block mb-1">Spätestens nach (Tagen)</label>
            <Input type="number" min="7" max="180" value={maxDays} onChange={e => setMaxDays(e.target.value)}
              data-testid="settings-checkin-max-days" className="bg-secondary border-border text-foreground rounded-sm h-9" />
          </div>
        </div>
      ) : (
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Wochen ohne Kontakt (1–12)</label>
          <Input type="number" min="1" max="12" value={weeks} onChange={e => setWeeks(e.target.value)}
            data-testid="settings-checkin-weeks" className="bg-secondary border-border text-foreground rounded-sm h-9 max-w-[140px]" />
        </div>
      ))}
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] text-muted-foreground">
          {autosend ? 'Versand werktags 9–17 Uhr über das Postfach der ursprünglichen Kampagne — Vorschau & Erledigt-Liste unter Broker Campaigns.'
            : 'Entwürfe erscheinen als Check-in-Einträge unter Broker Campaigns — Versand per Klick.'}
        </p>
        <Button onClick={save} disabled={saving || !loaded} data-testid="settings-checkin-save"
          className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm text-xs h-8 px-4 shrink-0">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Speichern'}
        </Button>
      </div>
    </div>
  );
}

/* ─────────────────────────── System (Admin) ─────────────────────────── */

function SystemTab() {
  const navigate = useNavigate();
  const links = [
    { to: '/system', icon: Wrench, iconClass: 'text-blue-400', title: 'System & Daten', desc: 'Meeting-Quoten, KI-Einstellungen, Import/Export, Bug-Reports, NDA-Referenzdokumente', testid: 'settings-goto-system' },
    { to: '/team', icon: Users, iconClass: 'text-emerald-400', title: 'Team & Rollen', desc: 'Mitglieder freischalten, Rollen und Berechtigungen verwalten', testid: 'settings-goto-team' },
    { to: '/sectors', icon: Layers, iconClass: 'text-violet-400', title: 'Sektoren', desc: 'Branchen-Taxonomie für das Deal-Screening pflegen', testid: 'settings-goto-sectors' },
    { to: '/scorecard-info', icon: Shield, iconClass: 'text-amber-400', title: 'Scorecard Info', desc: 'Kriterien und Gewichtungen der Phase-1/2-Scorecards', testid: 'settings-goto-scorecard' },
  ];
  return (
    <div className="space-y-3" data-testid="settings-system">
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <SettingsIcon className="w-3.5 h-3.5" /> Plattform-Administration — eigene Seiten, hier zentral verlinkt.
      </p>
      {links.map(l => (
        <button key={l.to} onClick={() => navigate(l.to)} data-testid={l.testid}
          className="w-full bg-card border border-border rounded-sm p-4 flex items-center justify-between gap-4 hover:bg-secondary/30 transition-colors text-left">
          <div className="flex items-start gap-3">
            <l.icon className={`w-5 h-5 shrink-0 mt-0.5 ${l.iconClass}`} strokeWidth={1.5} />
            <div>
              <p className="text-sm font-medium text-foreground">{l.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{l.desc}</p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────────── Persönlich (Benachrichtigungen) ─────────────────────────── */

function NotificationSettings({ isAcMember }) {
  const [triggers, setTriggers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [testingPush, setTestingPush] = useState(false);
  const [pushLoading, setPushLoading] = useState(true);
  const [pushStale, setPushStale] = useState(false);

  useEffect(() => {
    api.getNotificationSettings()
      .then(res => {
        setTriggers(res.data.triggers || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    // Check push subscription status
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then(reg => {
        reg.pushManager.getSubscription().then(sub => {
          setPushEnabled(!!sub);
          setPushLoading(false);
          // If the browser thinks it's subscribed, verify the server still has it.
          if (sub) {
            api.getPushStatus(sub.endpoint)
              .then(res => setPushStale(!res.data?.registered))
              .catch(() => {});
          }
        });
      }).catch(() => setPushLoading(false));
    } else {
      setPushLoading(false);
    }
  }, []);

  const handleTogglePush = async () => {
    if (!('serviceWorker' in navigator && 'PushManager' in window)) {
      toast.error('Push notifications not supported in this browser');
      return;
    }
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      if (pushEnabled) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await api.pushUnsubscribe(sub.endpoint);
          await sub.unsubscribe();
        }
        setPushEnabled(false);
        toast.success('Push notifications disabled');
      } else {
        const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
        await api.pushSubscribe(sub.toJSON());
        setPushEnabled(true);
        setPushStale(false);
        toast.success('Push notifications enabled!');
      }
    } catch (err) {
      toast.error('Failed: ' + (err.message || 'Permission denied'));
    }
    setPushLoading(false);
  };

  const handleTestPush = async () => {
    setTestingPush(true);
    try {
      const res = await api.sendTestPush();
      const { sent = 0, failed = 0, expired = 0 } = res.data || {};
      if (sent > 0) {
        toast.success(`Test notification sent to ${sent} device${sent === 1 ? '' : 's'}${failed ? ` (${failed} couldn't be reached)` : ''} — check your device`);
        if (expired > 0) setPushStale(true);
      } else if (expired > 0) {
        // The only subscription(s) are dead — clean up locally and prompt re-enable.
        try {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.getSubscription();
          if (sub) await sub.unsubscribe();
        } catch { /* ignore */ }
        setPushEnabled(false);
        setPushStale(true);
        toast.error('Your push subscription expired — please re-enable push notifications.');
      } else {
        toast.error('Could not deliver the test notification.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send test notification');
    }
    setTestingPush(false);
  };

  const handleToggle = (key) => {
    setTriggers(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateNotificationSettings(triggers);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="bg-card border border-border rounded-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-400" />
            <h2 className="font-heading text-lg text-foreground">Push Notifications</h2>
          </div>
          <div className="flex items-center flex-wrap gap-2">
            <button onClick={handleTogglePush} disabled={pushLoading}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs border transition-colors ${
                pushEnabled ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-secondary border-border text-muted-foreground hover:text-foreground'
              }`} data-testid="push-toggle-btn">
              {pushLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
              {pushEnabled ? 'Enabled' : 'Enable Push'}
            </button>
            {pushEnabled && (
              <button onClick={handleTestPush} disabled={testingPush}
                className="flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs border border-border text-muted-foreground hover:text-foreground transition-colors"
                data-testid="push-test-btn" title="Send a test notification to this device">
                {testingPush ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Send test
              </button>
            )}
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-4">
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Save
            </Button>
          </div>
        </div>

        {pushStale && (
          <div className="mb-5 flex items-center justify-between gap-3 bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 py-2" data-testid="push-stale-banner">
            <p className="text-xs text-amber-400">
              ⚠️ Your push subscription is no longer active on this device. Re-enable it to keep receiving notifications.
            </p>
            <button onClick={handleTogglePush} disabled={pushLoading}
              className="shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors"
              data-testid="push-reenable-btn">
              {pushLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
              Re-enable
            </button>
          </div>
        )}

        <PushDiagnosticPanel />

        {/* Scope: Favorites only vs All deals */}
        <div className="mb-6 p-4 bg-secondary/30 border border-border rounded-sm">
          <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-bold mb-3">Notification Scope</p>
          <div className="flex gap-2">
            <button onClick={() => setTriggers(prev => ({ ...prev, scope: 'favorites' }))}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-sm text-xs font-medium border transition-colors ${
                (triggers.scope || 'favorites') === 'favorites'
                  ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                  : 'bg-secondary border-border text-muted-foreground hover:text-foreground'
              }`}>
              <Star className="w-3.5 h-3.5" fill={(triggers.scope || 'favorites') === 'favorites' ? 'currentColor' : 'none'} />
              Favorites Only
            </button>
            <button onClick={() => setTriggers(prev => ({ ...prev, scope: 'all' }))}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-sm text-xs font-medium border transition-colors ${
                triggers.scope === 'all'
                  ? 'bg-blue-500/10 text-blue-400 border-blue-500/30'
                  : 'bg-secondary border-border text-muted-foreground hover:text-foreground'
              }`}>
              All Deals
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground/50 mt-2">
            {(triggers.scope || 'favorites') === 'favorites'
              ? 'You will only be notified for deals you have starred ⭐'
              : 'You will be notified for changes on all deals in the pipeline'}
          </p>
        </div>

        {/* Additive: also notify for claimed deals (hidden for AC members) */}
        {!isAcMember && (
        <div className="mb-6 flex items-start gap-3 p-4 bg-secondary/30 border border-border rounded-sm">
          <button onClick={() => handleToggle('notify_claimed')} role="switch" aria-checked={!!triggers.notify_claimed}
            data-testid="notif-toggle-notify_claimed"
            className={`w-11 h-6 rounded-full transition-colors relative shrink-0 mt-0.5 border ${triggers.notify_claimed ? 'bg-blue-600 border-blue-600' : 'bg-secondary border-border'}`}>
            <span className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow transition-all ${triggers.notify_claimed ? 'left-[22px]' : 'left-[3px]'}`} />
          </button>
          <div>
            <p className={`text-sm font-medium ${triggers.notify_claimed ? 'text-foreground' : 'text-muted-foreground'}`}>
              <span className="mr-1.5">📌</span>Deals I've claimed
            </p>
            <p className="text-[11px] text-muted-foreground/60 mt-0.5">Also notify me about changes on deals I own (claimed) — in addition to my favorites.</p>
          </div>
        </div>
        )}

        {/* Additive: pending reviews & decisions (hidden for AC members) */}
        {!isAcMember && (
        <div className="mb-6 flex items-start gap-3 p-4 bg-secondary/30 border border-border rounded-sm">
          <button onClick={() => handleToggle('pending_review')} role="switch" aria-checked={!!triggers.pending_review}
            data-testid="notif-toggle-pending_review"
            className={`w-11 h-6 rounded-full transition-colors relative shrink-0 mt-0.5 border ${triggers.pending_review ? 'bg-blue-600 border-blue-600' : 'bg-secondary border-border'}`}>
            <span className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow transition-all ${triggers.pending_review ? 'left-[22px]' : 'left-[3px]'}`} />
          </button>
          <div>
            <p className={`text-sm font-medium ${triggers.pending_review ? 'text-foreground' : 'text-muted-foreground'}`}>
              <span className="mr-1.5">📋</span>Pending Reviews
            </p>
            <p className="text-[11px] text-muted-foreground/60 mt-0.5">Notify me when a deal needs my decision — a proposed Pass / Contact / Approval, or a claim that has gone inactive.</p>
          </div>
        </div>
        )}

        <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-bold mb-3">Deal Updates (favorites / claimed)</p>

        <div className="space-y-4">
          {TRIGGER_INFO.map(t => <TriggerToggle key={t.key} t={t} triggers={triggers} onToggle={handleToggle} />)}
        </div>

        {!isAcMember && (<>
        <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-bold mb-3 mt-8">Workflow &amp; System Alerts</p>

        <div className="space-y-4">
          {SYSTEM_TRIGGER_INFO.map(t => <TriggerToggle key={t.key} t={t} triggers={triggers} onToggle={handleToggle} />)}
        </div>
        </>)}

        {isAcMember && (<>
        <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-bold mb-3 mt-8">Outreach Alerts (Opt-in)</p>

        <div className="space-y-4" data-testid="ac-outreach-optin-toggles">
          {SYSTEM_TRIGGER_INFO.filter(t => t.key === 'outreach' || t.key === 'landing')
            .map(t => <TriggerToggle key={t.key} t={t} triggers={triggers} onToggle={handleToggle} />)}
        </div>
        <p className="text-[11px] text-muted-foreground/60 mt-3">
          Als AC-Mitglied bekommst du Outreach-Pushes (QR-Scans, wiederholte Besuche, Audio/Video, Klicks,
          Formular-Antworten, Termin-Buchungen, E-Mail-Antworten, Opt-outs) nur, wenn du sie hier aktiv einschaltest.
        </p>
        </>)}
    </div>
  );
}


function TriggerToggle({ t, triggers, onToggle }) {
  const on = !!triggers[t.key];
  return (
    <div className="flex items-start gap-3">
      <button onClick={() => onToggle(t.key)} role="switch" aria-checked={on}
        data-testid={`notif-toggle-${t.key}`}
        className={`w-11 h-6 rounded-full transition-colors relative shrink-0 mt-0.5 border ${on ? 'bg-blue-600 border-blue-600' : 'bg-secondary border-border'}`}>
        <span className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? 'left-[22px]' : 'left-[3px]'}`} />
      </button>
      <div>
        <p className={`text-sm font-medium ${on ? 'text-foreground' : 'text-muted-foreground'}`}>
          <span className="mr-1.5">{t.icon}</span>{t.label}
        </p>
        <p className="text-[11px] text-muted-foreground/60 mt-0.5">{t.desc}</p>
      </div>
    </div>
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function bufToB64Url(buf) {
  const raw = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < raw.length; i++) s += String.fromCharCode(raw[i]);
  return window.btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function PushDiagnosticPanel() {
  const [open, setOpen] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [resetting, setResetting] = React.useState(false);
  const [deviceKey, setDeviceKey] = React.useState(null); // key this browser's sub was created with
  const [deviceEndpoint, setDeviceEndpoint] = React.useState('');
  const [detailed, setDetailed] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [fixing, setFixing] = React.useState(false);
  React.useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    api.getPushDiagnostic()
      .then(r => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then(reg => reg.pushManager.getSubscription()).then(sub => {
        if (sub) {
          setDeviceEndpoint(sub.endpoint || '');
          if (sub.options?.applicationServerKey) setDeviceKey(bufToB64Url(sub.options.applicationServerKey));
          else setDeviceKey('');
        } else setDeviceKey('');
      }).catch(() => setDeviceKey(''));
    } else setDeviceKey('');
  }, [open, data]);
  const t = data?.tally_last_30 || {};
  const envKey = (process.env.REACT_APP_VAPID_PUBLIC_KEY || '').trim();
  const serverKey = (data?.vapid_public_key || '').trim();
  const appKeyMatch = serverKey && envKey ? envKey === serverKey : null;
  const deviceKeyMatch = deviceKey && serverKey ? deviceKey === serverKey : null;
  const runDetailedTest = async () => {
    setDetailLoading(true);
    setDetailed(null);
    try {
      const r = await api.pushTestDetailed();
      setDetailed(r.data.results || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Einzeltest fehlgeschlagen'); }
    finally { setDetailLoading(false); }
  };
  const fixThisDevice = async () => {
    setFixing(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const oldSub = await reg.pushManager.getSubscription();
      if (oldSub) {
        try { await api.pushUnsubscribe(oldSub.endpoint); } catch { /* ignore */ }
        await oldSub.unsubscribe();
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(envKey),
      });
      await api.pushSubscribe(sub.toJSON());
      setDeviceKey(envKey);
      setDeviceEndpoint(sub.endpoint || '');
      setData(null);
      toast.success('Dieses Gerät wurde mit dem aktuellen Schlüssel neu registriert — jetzt "Send test" klicken.', { duration: 10000 });
    } catch (err) { toast.error('Neu-Registrierung fehlgeschlagen: ' + (err.message || '')); }
    finally { setFixing(false); }
  };
  const RESULT_LABEL = {
    accepted: ['angenommen (201)', 'text-emerald-400'],
    expired_deleted: ['abgelaufen → gelöscht', 'text-amber-400'],
    vapid_mismatch: ['403 SCHLÜSSEL-KONFLIKT', 'text-red-400'],
    failed: ['fehlgeschlagen', 'text-red-400'],
  };
  const resetAll = async () => {
    if (!window.confirm(`Alle Abos zurücksetzen?\n\nDamit werden alle ${data?.device_count || 0} registrierten Geräte-Abos gelöscht (viele davon sind alte Zombies aus vergangenen PWA/Browser-Sessions, an die Apple/FCM stumm nichts mehr ausliefern).\n\nDanach auf diesem Gerät oben "Enable" nochmal klicken → ein frisches, funktionierendes Abo wird registriert.`)) return;
    setResetting(true);
    try {
      const r = await api.pushResetAll();
      toast.success(`${r.data.deleted} Abos gelöscht — jetzt oben "Enable" klicken für ein frisches Abo auf diesem Gerät`, { duration: 12000 });
      setData(null); // force reload
    } catch (err) { toast.error(err.response?.data?.detail || 'Reset fehlgeschlagen'); }
    finally { setResetting(false); }
  };
  return (
    <div className="mb-5 border border-border rounded-sm" data-testid="push-diagnostic-panel">
      <button onClick={() => setOpen(v => !v)} data-testid="push-diagnostic-toggle"
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-secondary/30 transition-colors">
        <span className="text-xs text-muted-foreground">Diagnose (warum kommt keine Benachrichtigung an?)</span>
        <span className={`text-[10px] font-mono text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {open && (
        <div className="px-3 py-2 border-t border-border text-[11px] font-mono text-foreground/80 space-y-1">
          {loading && <p className="text-muted-foreground">lade …</p>}
          {data?.error && <p className="text-red-400">Diagnose fehlgeschlagen</p>}
          {data && !data.error && (<>
            <p>Angemeldete Geräte für diesen User: <b className={data.device_count > 0 ? 'text-emerald-400' : 'text-red-400'}>{data.device_count}</b></p>
            {(data.devices || []).map((d, i) => (
              <p key={i} className="text-muted-foreground pl-3">· {d.endpoint_host || 'unknown'} … {d.endpoint_tail} ({d.user_agent ? d.user_agent.slice(0, 60) : 'unknown UA'})</p>
            ))}
            <p className="pt-1">Letzte 30 Push-Versuche: <b className="text-emerald-400">{t.sent || 0} gesendet</b> · <b className="text-amber-400">{t.suppressed || 0} unterdrückt</b> · <b className="text-red-400">{t.failed || 0} fehlgeschlagen</b> · <b className="text-red-400">{t.no_subscription || 0} ohne Abo</b></p>
            <p>VAPID Backend-Key: <b className={data.vapid_ok ? 'text-emerald-400' : 'text-red-400'}>{data.vapid_ok ? 'ok' : 'MISSING'}</b></p>
            {serverKey && (
              <div className="pt-1 space-y-0.5" data-testid="push-key-check">
                <p>Schlüssel-Check (Server ↔ App ↔ Gerät):</p>
                <p className="pl-3 text-muted-foreground">· Server-Key: {serverKey.slice(0, 14)}…</p>
                <p className="pl-3">· App-Build-Key: {appKeyMatch === null ? <span className="text-muted-foreground">unbekannt</span> : appKeyMatch ? <b className="text-emerald-400">stimmt überein</b> : <b className="text-red-400">WEICHT AB ({envKey.slice(0, 14)}…)</b>}</p>
                <p className="pl-3">· Abo dieses Geräts: {deviceKey === null ? <span className="text-muted-foreground">prüfe…</span> : deviceKey === '' ? <span className="text-amber-400">kein Abo in diesem Browser</span> : deviceKeyMatch ? <b className="text-emerald-400">stimmt überein</b> : <b className="text-red-400">VERALTETER SCHLÜSSEL — Apple verwirft Pushes an dieses Gerät still!</b>}</p>
                {deviceKey && deviceEndpoint && (
                  <p className="pl-3 text-muted-foreground">· Endpoint dieses Geräts: …{deviceEndpoint.slice(-20)} {(data.devices || []).some(d => deviceEndpoint.endsWith(d.endpoint_tail)) ? <span className="text-emerald-400">(auf Server registriert)</span> : <b className="text-red-400">(NICHT auf Server registriert!)</b>}</p>
                )}
                {deviceKeyMatch === false && (
                  <button onClick={fixThisDevice} disabled={fixing} data-testid="push-fix-device-btn"
                    className="mt-1 inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors disabled:opacity-60">
                    {fixing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
                    Dieses Gerät mit aktuellem Schlüssel neu registrieren
                  </button>
                )}
              </div>
            )}
            <div className="pt-2 border-t border-border mt-2">
              <button onClick={runDetailedTest} disabled={detailLoading} data-testid="push-detailed-test-btn"
                className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-sm border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 transition-colors disabled:opacity-60">
                {detailLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Einzeltest an jedes Gerät (zeigt echten Apple/FCM-Status)
              </button>
              {detailed && (
                <div className="mt-2 space-y-1" data-testid="push-detailed-results">
                  {detailed.map((r, i) => {
                    const [label, cls] = RESULT_LABEL[r.result] || [r.result, 'text-muted-foreground'];
                    const isThisDevice = deviceEndpoint && deviceEndpoint.endsWith(r.endpoint_tail);
                    return (
                      <p key={i} className="text-muted-foreground pl-1">
                        {i + 1}. {r.endpoint_host} …{r.endpoint_tail} → <b className={cls}>{label}</b>
                        {isThisDevice && <b className="text-blue-300"> ← DIESES GERÄT</b>}
                        {r.detail ? <span className="text-red-400/70"> — {r.detail.slice(0, 80)}</span> : ''}
                      </p>
                    );
                  })}
                  <p className="text-amber-300 pt-1">
                    Wichtig: &bdquo;angenommen (201)&ldquo; heißt nur, dass Apple/FCM die Push angenommen hat. Kommt beim Einzeltest
                    trotzdem nichts auf dem iPhone an, ist das Abo ein stiller Zombie → unten alle Abos zurücksetzen,
                    die PWA vom Home-Bildschirm <b>löschen und neu hinzufügen</b>, dann Push neu aktivieren.
                  </p>
                </div>
              )}
            </div>
            {(data.recent_attempts || []).slice(0, 8).map((l, i) => (
              <p key={i} className="text-muted-foreground pl-3">· {(l.created_at || '').slice(0, 19).replace('T', ' ')} · {l.action || '?'} · <span className={l.status === 'sent' ? 'text-emerald-400' : l.status === 'failed' ? 'text-red-400' : 'text-amber-400'}>{l.status}</span> {l.error ? ` — ${l.error.slice(0, 60)}` : ''}</p>
            ))}
            {data.device_count === 0 && (
              <p className="text-red-400 mt-2">→ Keine Geräte angemeldet. Klicke oben auf &bdquo;Enable Push&ldquo; um dieses Gerät neu zu registrieren.</p>
            )}
            {data.device_count > 0 && (t.sent || 0) === 0 && (t.no_subscription || 0) === 0 && (
              <p className="text-amber-400 mt-2">→ Keine Push-Versuche in den letzten 30 Log-Einträgen. Prüfe unten die Trigger-Toggles — möglicherweise alle deaktiviert.</p>
            )}
            {data.device_count >= 3 && (t.sent || 0) > 0 && (
              <div className="mt-3 border-t border-border pt-2 space-y-1">
                <p className="text-amber-300">
                  → Verdacht: <b>{data.device_count} Geräte</b> registriert, davon sind wahrscheinlich viele „Zombies" aus alten PWA/Browser-Sessions.
                  Apple/FCM antworten für tote Abos oft trotzdem mit &bdquo;201 sent&ldquo;, weshalb der Server nicht merkt, dass die Push ins Leere geht.
                </p>
                <button onClick={resetAll} disabled={resetting} data-testid="push-reset-all-btn"
                  className="mt-1 inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-sm border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-60">
                  {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
                  Alle {data.device_count} Abos zurücksetzen &amp; neu registrieren
                </button>
              </div>
            )}
          </>)}
        </div>
      )}
    </div>
  );
}
